import type { FilterClause } from 'tio-common/utils/open-search-builder';

/**
 * Facet-bucket definitions for the partner-admin people page.
 *
 * The tio-admin sibling (`tio-admin/app/utils/people-facets.ts`) facets on the
 * PERSON-grain root fields — `person_is_active_employee_boolean`,
 * `all_jurisdictions_keyword` — which are computed across every employer a
 * person has. A partner admin may only see their own company's slice, so every
 * bucket here targets the company-scoped equivalent inside `employees_nested`
 * (see tio-api `person_employee_builder.rb#construct_person_employee_object`).
 *
 * Using the root fields on this page would be both wrong (a person deactivated
 * here but active elsewhere would read as active) and a cross-tenant leak.
 */

/** Nested path holding one entry per company the person is employed at. */
export const EMPLOYEES_PATH = 'employees_nested';

/**
 * Employment status at THIS company. `employee_is_active_boolean` is genuinely
 * per-company (unlike the enrollment flags below). The negative bucket uses
 * must_not so documents missing the field still partition.
 */
export const EMPLOYEE_STATUS_BUCKETS = {
  active: { term: { 'employees_nested.employee_is_active_boolean': true } },
  inactive: {
    bool: { must_not: [{ term: { 'employees_nested.employee_is_active_boolean': true } }] },
  },
} satisfies Record<string, FilterClause>;

/**
 * Login state. Deliberately OVERLAPPING (ever ⊇ active), mirroring tio-admin:
 * - activeEnrolled: has an ACTIVE Tuition.io login
 * - everEnrolled:   has ever registered a login (may now be inactive)
 * - neverEnrolled:  complement of everEnrolled
 *
 * The indexer stamps these onto every nested entry from person-level data, so
 * they read the same at every employer — login is person-grain by nature. They
 * are scoped here only so they compose with the other nested facet clauses.
 */
export const ENROLLMENT_BUCKETS = {
  activeEnrolled: { term: { 'employees_nested.employee_is_registered_boolean': true } },
  everEnrolled: { term: { 'employees_nested.employee_is_enrolled_boolean': true } },
  neverEnrolled: {
    bool: { must_not: [{ term: { 'employees_nested.employee_is_enrolled_boolean': true } }] },
  },
} satisfies Record<string, FilterClause>;

/**
 * Benefit participation at this company. Has no tio-admin equivalent — it is
 * the filter the partner page already shipped, promoted to a counted facet.
 * The index stores the status lowercased (tio-api `Employee#participation_status`).
 */
export const PARTICIPATION_BUCKETS = {
  participating: { term: { 'employees_nested.participation_status_keyword': 'participating' } },
  pending: { term: { 'employees_nested.participation_status_keyword': 'pending' } },
  deactivated: { term: { 'employees_nested.participation_status_keyword': 'deactivated' } },
} satisfies Record<string, FilterClause>;

export { PARTNER_VISIBLE_ROLE_KEYS } from 'tio-common/utils/partner-roles';

/** Scope a nested clause to one company's employee entry. */
export function companyEmployeeClause(companyId: string): FilterClause {
  return { term: { 'employees_nested.company_id_keyword': companyId } };
}

/** OR the selected named buckets into one clause (undefined when none/unknown). */
export function bucketSelectionClause(
  buckets: Record<string, FilterClause>,
  csv: string
): FilterClause | undefined {
  const selected = csv
    .split(',')
    .filter(Boolean)
    .map((key) => buckets[key])
    .filter((clause): clause is FilterClause => Boolean(clause));

  if (!selected.length) return undefined;
  if (selected.length === 1) return selected[0];
  return { bool: { should: selected, minimum_should_match: 1 } };
}

/**
 * Wrap a clause built from `employees_nested.*` fields so it can sit in a
 * top-level bool/post_filter. Every nested facet selection needs this — a bare
 * dotted-path term at the root matches nothing against a `nested` mapping.
 */
export function nestedEmployeeClause(clauses: FilterClause[]): FilterClause {
  return {
    nested: {
      path: EMPLOYEES_PATH,
      query: { bool: { filter: clauses } },
    },
  } as FilterClause;
}
